import React, { useState } from "react";
import Index from "../../../Index";
import PagesIndex from "../../../PagesIndex";
import "./news.css";
import "./news.responsive.css";
import { debounce } from "@mui/material/utils";
import parse from "autosuggest-highlight/parse";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PropTypes from "prop-types";

const AutoCompleteLocation = ({
  initialValues,
  setFieldValue,
  errors,
  touched,
  values,
}) => {
  const autocompleteService = React.useRef(null);
  const placeService = React.useRef(null);
  const [placeInputValue, setPlaceInputValue] = useState([]);
  const [placeOptions, setPlaceOptions] = useState([]);
  const fetch = React.useMemo(
    () =>
      debounce((request, callback) => {
        if (autocompleteService.current) {
          autocompleteService.current.getPlacePredictions(
            request,
            (predictions, status) => {
              if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
                console.error("Error fetching place predictions:", status);
                callback([]);
                return;
              }
              callback(predictions);
            }
          );
        }
      }, 400),
    []
  );
  React.useEffect(() => {
    if (!autocompleteService.current && window.google) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
    }

    if (!autocompleteService.current) return;

    const inputValue =
      typeof placeInputValue === "string" ? placeInputValue.trim() : "";

    if (inputValue.length === 0) {
      setPlaceOptions(initialValues.location ? [initialValues.location] : []);
      return;
    }

    fetch({ input: inputValue }, (results) => {
      let newOptions = initialValues.location ? [initialValues.location] : [];
      if (results) {
        newOptions = [...newOptions, ...results];
      }
      setPlaceOptions(newOptions);
    });
  }, [placeInputValue, fetch, initialValues.location]);

  return (
    <Index.Box className="admin-form-group">
      <Index.Autocomplete
        fullWidth
        size="small"
        id="google-map-demo"
        className="admin-form-control admin-auto-complete-control"
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.description
        }
        isOptionEqualToValue={(option, value) =>
          option.description === value.location
        }
        filterOptions={(x) => x}
        options={placeOptions?.length ? placeOptions : []}
        autoComplete
        closeText={""}
        openText={""}
        clearText={""}
        includeInputInList
        filterSelectedOptions
        disableClearable={initialValues?.location ? false : true}
        value={initialValues?.location}
        noOptionsText="No locations"
        onChange={(event, newValue) => {
          setPlaceOptions(
            newValue ? [newValue, ...placeOptions] : placeOptions
          );

          if (newValue === null) {
            setFieldValue("location", "");
            setFieldValue("latitude", "");
            setFieldValue("longitude", "");
          } else {
            setFieldValue("location", newValue?.description);
            // Get lat and lng from place details
            const request = {
              placeId: newValue?.place_id,
              fields: ["geometry", "address_components"],
            };

            placeService.current.getDetails(request, (place, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                const locality = place?.address_components?.filter((ele) =>
                  ele?.types?.includes("locality")
                );
                setFieldValue("city", locality?.[0]?.long_name);

                let latitude = place.geometry.location.lat();
                let longitude = place.geometry.location.lng();
                setFieldValue("latitude", latitude);
                setFieldValue("longitude", longitude);
              }
            });
          }
        }}
        onInputChange={(event, newInputValue) => {
          setPlaceInputValue(newInputValue);
        }}
        renderInput={(params) => (
          <Index.TextField
            size="small"
            {...params}
            // label="Address"
            id="location"
            name="location"
            fullWidth
            placeholder="Enter Location"
            className="admin-form-control"
          />
        )}
        renderOption={(props, option) => {
          const matches =
            option.structured_formatting.main_text_matched_substrings || [];

          const parts = parse(
            option.structured_formatting.main_text,
            matches.map((match) => [match.offset, match.offset + match.length])
          );

          return (
            <li {...props}>
              <Index.Grid container alignItems="center">
                <Index.Grid item sx={{ display: "flex", width: 44 }}>
                  <LocationOnIcon sx={{ color: "text.secondary" }} />
                </Index.Grid>
                <Index.Grid
                  item
                  sx={{
                    width: "calc(100% - 44px)",
                    wordWrap: "break-word",
                  }}
                >
                  {parts.map((part, index) => (
                    <Index.Box
                      key={part.id}
                      component="span"
                      sx={{
                        fontWeight: part.highlight ? "bold" : "regular",
                      }}
                    >
                      {part.text}
                    </Index.Box>
                  ))}

                  <Index.Typography variant="body2" color="text.secondary">
                    {option.structured_formatting.secondary_text}
                  </Index.Typography>
                </Index.Grid>
              </Index.Grid>
            </li>
          );
        }}
      />
      {touched.location && errors.location && (
        <PagesIndex.FormHelperText className="admin-error-text">
          {errors.location}
        </PagesIndex.FormHelperText>
      )}
    </Index.Box>
  );
};

export default AutoCompleteLocation;

AutoCompleteLocation.propTypes = {
  initialValues: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
};
